import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, lazy } from "react";
import { faqSchema } from "@/components/PageFaq";
import { HOME_FAQ } from "@/lib/home-faq";
import { PageExpansion } from "@/components/PageExpansion";


const HomeDatabaseSections = lazy(() =>
  import("@/components/HomeDatabaseSections").then((m) => ({ default: m.HomeDatabaseSections })),
);

// No ampersands: escaped as &amp; in the rendered <head> they push the audited
// title length past the 60-character SERP budget.
const TITLE = "Pal Stars: Palworld TCG Price Index, Price Chart & Wiki";
const DESCRIPTION =
  "Palworld wiki and TCG database: daily Palworld TCG Price Index, per-card price chart, breeding calculator, mount speeds, boss damage math and guides.";
const SOCIAL_IMAGE =
  "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/17714a28-4a57-4e59-8359-909140a1f8f4/id-preview-3bac5354--a3729fa2-e85b-4f5b-84b2-0934e880c580.lovable.app-1785103564262.png";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      // Homepage-only social image (the root route no longer emits a default).
      { property: "og:image", content: SOCIAL_IMAGE },
      { property: "og:image:alt", content: "Pal Stars — Palworld wiki and card database" },
      { name: "twitter:image", content: SOCIAL_IMAGE },

      { name: "robots", content: "index, follow, max-image-preview:large" },
      { name: "impact-site-verification", value: "65801d27-986a-4115-9032-78f5bab32684" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "VideoGame",
          name: "Palworld",
          publisher: { "@type": "Organization", name: "Pocketpair, Inc.", url: "https://www.pocketpair.jp/" },
          author: { "@type": "Organization", name: "Pocketpair, Inc.", url: "https://www.pocketpair.jp/" },
          genre: ["Open World Survival", "Creature Collector", "Crafting"],
          gamePlatform: ["PC", "Xbox Series X|S", "PlayStation 5"],
        }),
      },
      // WebSite + SearchAction markup lives on the root route only.
      faqSchema(HOME_FAQ, "https://palstars.com/"),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": "https://palstars.com/#webpage",
          url: "https://palstars.com/",
          name: TITLE,
          description: DESCRIPTION,
          speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: ["h1", "h2", ".aeo-answer"],
          },
        }),
      },

      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: { "@id": "https://palstars.com/", name: "Home" } },
          ],
        }),
      },
    ],
  }),
  // Rotation seed is decided on the server and handed to the client, so the
  // featured/trending/guide picks are identical in the SSR HTML and after
  // hydration (no post-hydration reshuffle, no layout shift).
  loader: () => ({ seed: String(Math.floor(Date.now() / 600_000)) }),
  component: Home,
});


function Home() {
  const { seed } = Route.useLoaderData();
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-center gap-6 px-4 py-10 sm:py-16">
      {/* Brand hero */}
      <div className="text-center">
        <h1 className="text-4xl font-black tracking-tight text-foreground sm:text-5xl">Pal Stars</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Palworld TCG Price Index, Cards, and Palworld Wiki
        </p>
      </div>

      {/* Quick-access tabs */}
      <nav aria-label="Quick access" className="flex w-full flex-wrap justify-center gap-2">
        <Link to="/tcg-price-index" className="rounded-full border border-primary/50 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/20 sm:text-sm">
          Price Index
        </Link>
        <Link to="/prices" className="glass rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-primary/10 sm:text-sm">
          Price Chart
        </Link>
        <Link to="/card-marketplace" className="glass rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-primary/10 sm:text-sm">
          TCG Cards
        </Link>
        <Link to="/marketplace" className="glass rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-primary/10 sm:text-sm">
          Marketplace
        </Link>
      </nav>

      {/* Centered smart search */}
      <form action="/pals" method="get" className="flex w-full gap-2">
        <label htmlFor="home-search" className="sr-only">Search cards, Pals, sets, values or guides</label>
        <input
          id="home-search"
          name="q"
          autoComplete="off"
          aria-label="Search cards, Pals, sets, values or guides"
          placeholder="Search cards, Pals, values or guides…"
          className="glass flex-1 rounded-full px-5 py-3 text-sm outline-none ring-primary focus:ring-2"
        />
        <button className="rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:brightness-110">
          Search
        </button>
      </form>

      {/* Mass copy/paste bulk list appraiser */}
      <details className="glass w-full rounded-2xl p-4">
        <summary className="cursor-pointer text-sm font-semibold text-muted-foreground">
          Mass copy/paste bulk list appraiser
        </summary>
        <form action="/tcg-bulk-order" method="get" className="mt-3 flex flex-col gap-3">
          <p className="text-xs text-muted-foreground">
            Paste your list, one card per line (for example <code>1x EBP01-006</code>). Every printed code is matched
            against the official card database, then priced before you buy.
          </p>
          <label htmlFor="home-bulk-list" className="sr-only">Bulk card list</label>
          <textarea
            id="home-bulk-list"
            name="list"
            rows={4}
            placeholder={"1x EBP01-006\n2x ESOUL-002"}
            className="w-full rounded-lg border border-border bg-background/60 p-2 font-mono text-xs outline-none ring-primary focus:ring-2"
          />
          <button className="rounded-lg border border-primary/60 bg-primary/10 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/20">
            Appraise bulk list
          </button>
        </form>
      </details>

      {/* Collapsible SEO vault: keeps the full crawlable database index in the HTML */}
      <details className="glass w-full rounded-2xl p-4">
        <summary className="cursor-pointer text-sm font-semibold text-primary">
          About the Palworld Official Card Game index &amp; tools
        </summary>
        <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">
          <p className="aeo-answer">
            Pal Stars tracks the Palworld Official Card Game economy: live marketplace quotes, price history and
            trading volume feed a daily Palworld card value ledger and the Palworld TCG Price Index.
          </p>
          <p>
            Use the collection tools to review the Palworld TCG price index, follow daily price changes, run pack
            probabilities in the pack calculator, and preview bulk order data before you trade.
          </p>
        </div>
        <div className="mt-4">
          <Suspense fallback={<div className="min-h-[200px]" aria-hidden="true" />}>
            <HomeDatabaseSections seed={seed} />
          </Suspense>
        </div>
        <div className="mt-4">
          <PageExpansion path="/" />
        </div>
      </details>
    </div>
  );
}
